import { useEffect, type ReactNode } from 'react';
import { useLocation, useNavigate, type Location } from 'react-router-dom';

/**
 * 목록은 그대로 두고 오른쪽에서 슬라이드로 열리는 주문 상세 패널의 껍데기.
 *
 * App.tsx 의 "background location" 패턴과 짝이다 — 목록에서 <Link state={{ background }}>
 * 로 열면 뒤에 깔린 목록은 살아있는 컴포넌트라, 닫아도 다시 조회하지 않는다.
 *
 * 랜덤박스(/randombox/orders)와 くじ(/lottery/orders) 주문 상세가 이 껍데기를 함께
 * 쓴다. 안쪽 내용(조회·폼·저장)은 각 모듈의 OrderEditor 가 그린다 — 두 주문의
 * 데이터 구조가 다르므로 껍데기만 공유한다.
 */
export function OrderDrawer({
  onClose,
  children,
  className,
}: {
  onClose: () => void;
  children: ReactNode;
  /** 패널에 덧붙일 클래스. 안쪽 화면이 패널 여백·폭을 자기에 맞게 잡을 때 쓴다. */
  className?: string;
}) {
  useEffect(() => {
    function onKeydown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKeydown);
    return () => window.removeEventListener('keydown', onKeydown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="drawer-overlay" onClick={onClose}>
      <div className={className ? `drawer-panel ${className}` : 'drawer-panel'} onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

/**
 * 패널을 닫고 목록으로 돌아가는 함수를 만든다.
 *
 * 목록에서 열 때 넘겨준 background 위치(페이지·필터가 담긴 검색어까지)로 돌아간다.
 * 그냥 목록 경로로 고정하면 3페이지에서 열었다가 닫아도 1페이지로 리셋됐다.
 * background 가 없으면(주소를 새로고침 등으로 직접 연 경우) fallbackPath 로 보낸다.
 */
export function useDrawerClose(fallbackPath: string): () => void {
  const navigate = useNavigate();
  const location = useLocation();
  const background = (location.state as { background?: Location } | null)?.background;

  return function close() {
    navigate(background ? `${background.pathname}${background.search}` : fallbackPath);
  };
}
